/**
 * B31 — presentational components, deliberately pure: every screen is a
 * function of props with callbacks out, so the §7 walk-through is testable
 * as markup without a browser. Single-column layout — §9.2 requires the
 * same markup to hold at embed widths.
 *
 * B20b rebased: products are buyer cards (variants with channel-resolved
 * display prices, §10 display), cart lines carry the server's lineId so
 * removal is drivable (B20b(c)), payment renders the real gateway state,
 * and confirmed tickets show their signed token (B20b(e); QR image rides
 * the TRA-35 ruling — same stance as B28's email body).
 */
import { useState } from "react";
import type { ReactElement, ReactNode } from "react";
import type { LineupNames, PolicyFacts } from "@transact/contracts";
import {
  Banner,
  Button,
  EmptyState,
  Input,
  PickerTile,
  CartChip,
  GlassHeader,
  ProductCard,
  Stepper as UiStepper,
} from "@transact/ui";
import { type BuyerVocabulary, cardAction, cardMeta, chipFact, flagshipProductId, priceFace } from "../lib/bookingFace.js";
import { seatsLeft } from "../lib/browse.js";
import { formatMoney, type Money } from "../lib/money.js";
import { busyWords, cartActKey, doorBusy } from "../lib/busyFace.js";
import type { FlowState } from "../lib/flow.js";
import { slotDisplay } from "../lib/confirmation.js";
import {
  canAdd,
  compositionIssues,
  compositionLines,
  issueHint,
  maxQuantity,
  quantityOf,
  type Composition,
} from "../lib/composition.js";

/* ------------------------------------------------------------ vocabulary */

/** Buyer-facing variant card (B20b(a)): price is display-only, resolved by the channel. */
export interface VariantCard {
  readonly variantId: string;
  readonly name: string;
  readonly displayPrice?: Money;
  /** B20b(a) constraints on the card — B31h's composer walls/hints read them. */
  readonly constraints?: {
    readonly minPerOrder?: number;
    readonly maxPerOrder?: number;
    readonly requiresVariantId?: string;
  };
}

export interface ProductSummary {
  readonly productId: string;
  readonly name: string;
  /** B31c (TRA-52): the product→schedule binding the departure step reads. */
  readonly scheduleIds: readonly string[];
  readonly variants: readonly VariantCard[];
  /** B31h (TRA-80): buyer-flow order config; absent = SLOT_FIRST. */
  readonly presentation?: {
    readonly flowOrder?: "SLOT_FIRST" | "TICKETS_FIRST";
    /** B57: O-D descriptor for journey products — the route planner reads it. */
    readonly journey?: {
      readonly from: { readonly key: string; readonly name: string };
      readonly to: { readonly key: string; readonly name: string };
      readonly legs: readonly string[];
    };
    /** B521: catalog.list carries photos[0] only; catalog.get every photo. */
    readonly photos?: readonly { readonly url: string; readonly alt?: string; readonly credit?: string }[];
    /** B521: the whole description — catalog.get only. */
    readonly description?: string;
    /** B521: the description's first sentence, derived server-side — the card's line. */
    readonly summary?: string;
    /** B615: the show's policy facts — the pay step's acknowledgement reads them. */
    readonly policies?: PolicyFacts;
    /** B617: the show's lineup — the poster draws the headliner and lineupLine. */
    readonly lineup?: LineupNames;
  };
}

export interface SlotSummary {
  readonly slotId: string;
  readonly start: number;
  readonly end: number;
  readonly status: string;
  /** B615 leg 3b — this occurrence's own age (its schedule's row); absent = the product's age stands. */
  readonly minAge?: number;
}

export interface PoolAvailability {
  readonly poolId: string;
  readonly available: number;
}

/** Cart lines mirror cart.get (B20b(c)) — the server's lineId makes removal real. */
export interface CartLine {
  readonly lineId: string;
  readonly productId: string;
  readonly productName: string;
  readonly variantId?: string;
  readonly slotId?: string;
  /** B170 (§7): the departure instant, when the line rides one. */
  readonly slotStart?: number;
  readonly quantity: number;
  /** B170 (§10.1): what the line costs before promotions. */
  readonly amount?: Money;
}

/** B170 (§10.2): a fired promotion, NAMED, with its signed (≤ 0) amount. */
export interface CartAdjustment {
  readonly promotionId: string;
  readonly label: string;
  readonly amount: Money;
}

export interface EntitlementSummary {
  readonly entitlementId: string;
  readonly state: string;
  /** Signed ttk1 ticket token (B20b(e)); absent while it loads or on refusal. */
  readonly token?: string;
}

/** Which screen a flow state shows — pure, exhaustive. */
export type ScreenKind = "browse" | "cart" | "checkout_in_flight" | "payment" | "confirmed" | "expired";

export function pickScreen(state: FlowState): ScreenKind {
  switch (state.phase) {
    case "BROWSE":
      return "browse";
    case "CART":
      return "cart";
    case "CHECKOUT_IN_FLIGHT":
      return "checkout_in_flight";
    case "PENDING_PAYMENT":
      return "payment";
    case "CONFIRMED":
      return "confirmed";
    case "EXPIRED":
      return "expired";
  }
}


/* ---------------------------------------------------------------- shell */

interface Step {
  readonly label: string;
  readonly kinds: readonly ScreenKind[];
}

/** §7 as the buyer walks it; holding seats and paying are one step to them. */
const STEPS: readonly Step[] = [
  { label: "Tickets", kinds: ["browse"] },
  { label: "Cart", kinds: ["cart"] },
  { label: "Pay", kinds: ["checkout_in_flight", "payment"] },
  { label: "Done", kinds: ["confirmed"] },
];

export function Stepper(props: { readonly current: ScreenKind }): ReactElement {
  return (
    <ol className="steps">
      {STEPS.map((step) => (
        <li key={step.label} {...(step.kinds.includes(props.current) ? { "aria-current": "step" as const } : {})}>
          {step.label}
        </li>
      ))}
    </ol>
  );
}

/** Loading, announced: role=status reaches assistive tech politely. */
export function Busy(props: { readonly label: string }): ReactElement {
  return (
    <p className="busy" role="status">
      {props.label}
    </p>
  );
}

/** Notices: information is polite, errors interrupt (role=alert). */
export function Notice(props: {
  readonly kind?: "info" | "error";
  readonly children: ReactNode;
  /** B551 — the failure's reference id; the package Banner draws it verbatim. */
  readonly reference?: string | undefined;
}): ReactElement {
  /* B62 re-skin: the ui Banner carries the tone AND the right live-region
     role (danger interrupts, info is polite) — one component, no drift. */
  return (
    <Banner tone={props.kind === "error" ? "danger" : "info"} reference={props.reference}>
      {props.children}
    </Banner>
  ) as ReactElement;
}

/* -------------------------------------------------------------- screens */


/**
 * B169(a) — the face of a booking page that has no business behind it.
 * Opened bare, or with only a channel key the browser cannot resolve,
 * there is nothing to ask for: no catalog to be empty, no request to
 * fail. It is ONE teaching state, never stacked with a refusal banner and
 * never wearing the empty-catalog sentence, which would be a lie about a
 * catalog nobody looked at. Both reasons share the heading (the buyer's
 * situation is identical) and differ only in the next step.
 */
export function NotConnectedView(props: {
  readonly reason: "no-context" | "site-key-unresolved";
}): ReactElement {
  const body =
    props.reason === "site-key-unresolved"
      ? "This link points at a business that can't be looked up from here, so there's nothing to show yet. If someone sent you this link, ask them for the full booking link. If the page is yours, finish connecting it in your booking settings."
      : "This link doesn't say which business it belongs to, so there's nothing to show yet. If someone sent you this link, ask them for the full booking link. If the page is yours, finish connecting it in your booking settings.";
  return <EmptyState title="This booking page isn't connected to a business yet" body={body} /> as ReactElement;
}

/**
 * B31h (TRA-80): ONE pick per card — quantities compose on the next screen,
 * so a card click never puts anything in the cart. Variant prices stay on
 * the card as §10 display information.
 */
export function ProductList(props: {
  readonly products: readonly ProductSummary[];
  /** B170: per-card variant price facts — "from <min>" when the journey family prices a ticket differently. */
  readonly priceFacts?: ReadonlyMap<string, ReadonlyMap<string, { readonly amount: Money; readonly from: boolean }>>;
  /** B403 #26 (e) — per-card GREEN live fact ("Next departure 10:30 · 36 seats left"),
   * derived from a real departures read; absent card = no read answered, no chip invented. */
  readonly nextFacts?: ReadonlyMap<string, string>;
  /** B614 — the tenant's buyer voice: SHOW names the card action ("Get tickets") and drops the departure clause. */
  readonly vocab?: BuyerVocabulary | undefined;
  readonly onPick: (productId: string) => void;
}): ReactElement {
  if (props.products.length === 0)
    return (
      <EmptyState
        title="Nothing is on sale right now"
        body="Check back soon — tickets appear here the moment they go on sale."
      />
    );
  /* B397 (75:2): EXACTLY ONE Button/primary on the whole grid — the flagship
     is the catalog's first product; every other card gets the outline kind.
     Only this list can see the whole grid, so the rule is held (and walled)
     here rather than in the card. */
  const flagship = flagshipProductId(props.products);
  return (
    <ul className="product-grid" data-redline="booking-grid">
      {props.products.map((p, i) => {
        const act = cardAction(p, props.vocab);
        const price = priceFace(p, props.priceFacts?.get(p.productId));
        const meta = cardMeta(p, props.vocab);
        /* B403 #26 (e) — the live green fact outranks the neutral note:
           75:2's flagship wears "Next sailing · seats left", and a note
           about an add-on rule belongs to the card whose face has room. */
        const live = props.nextFacts?.get(p.productId);
        const chip = live !== undefined ? ({ dot: "good", label: live } as const) : chipFact(p);
        return (
          <li key={p.productId}>
            {/* B397: the card's three zones are the package ProductCard's; the
                verb is the product's own (§10 — booked when it rides departures,
                bought otherwise), and every meta/chip fact comes from a read. */}
            <ProductCard
              title={p.name}
              {...(meta !== undefined ? { meta } : {})}
              price={
                price.kind === "quote"
                  ? { kind: "quote" }
                  : { kind: price.kind, display: formatMoney(price.amount) }
              }
              {...(chip !== undefined ? { chip } : {})}
              {...(p.presentation?.photos?.[0] !== undefined
                ? {
                    image: {
                      url: p.presentation.photos[0].url,
                      ...(p.presentation.photos[0].alt !== undefined ? { alt: p.presentation.photos[0].alt } : {}),
                    },
                  }
                : {})}
              {...(p.presentation?.summary !== undefined ? { summary: p.presentation.summary } : {})}
              action={{
                label: act.label,
                onPress: () => props.onPick(p.productId),
                ...(p.productId === flagship ? { primary: true } : {}),
                ...(act.ariaLabel !== undefined ? { ariaLabel: act.ariaLabel } : {}),
              }}
              {...(i === 0 ? { redline: "booking-card-1" } : {})}
            />
          </li>
        );
      })}
    </ul>
  );
}

/**
 * B31h (TRA-80) — the party composer: a bounded stepper per variant with
 * live line prices, §4.2 constraint hints in buyer language WHILE the
 * quantities are set, and one Add for the whole party. The walls are
 * inline: − dead at zero, + dead at maxPerOrder, Add dead while a hint
 * stands. slotLabel names the chosen departure (SLOT_FIRST); addLabel
 * relabels the action for TICKETS_FIRST ("Choose a time").
 */
export function CompositionView(props: {
  readonly card: ProductSummary;
  readonly comp: Composition;
  readonly slotLabel?: string;
  readonly addLabel?: string;
  readonly onChange: (variantId: string, quantity: number) => void;
  readonly onAdd: () => void;
}): ReactElement {
  const { card, comp } = props;
  const lines = compositionLines(card, comp);
  const issues = compositionIssues(card, comp);
  return (
    <div className="composition">
      {props.slotLabel !== undefined ? <p className="chosen-slot">Departure: {props.slotLabel}</p> : null}
      <ul className="composer">
        {card.variants.map((v) => {
          const qty = quantityOf(comp, v.variantId);
          const line = lines.find((l) => l.variantId === v.variantId);
          return (
            <li key={v.variantId} className="composer-row">
              <span className="variant-name">
                {v.name}
                {v.displayPrice !== undefined ? (
                  <span className="price"> · {formatMoney(v.displayPrice)} each</span>
                ) : null}
              </span>
              {/* B62 re-skin: the quantity control IS the ui Stepper — same walls
                  (− dead at zero, + dead at maxPerOrder), buyer-language labels. */}
              <UiStepper
                label={v.name}
                value={qty}
                min={0}
                max={maxQuantity(card, v.variantId)}
                onChange={(next) => props.onChange(v.variantId, next)}
                decreaseLabel={`Fewer ${v.name}`}
                increaseLabel={`More ${v.name}`}
              />
              {line?.linePrice !== undefined ? <span className="line-price">{formatMoney(line.linePrice)}</span> : null}
            </li>
          );
        })}
      </ul>
      {issues.length > 0 ? (
        <div id="add-issues">
          {issues.map((issue) => (
            <p key={`${issue.kind}:${issue.variantId}`} className="hint" role="status">
              {issueHint(issue, card)}
            </p>
          ))}
        </div>
      ) : null}
      {/* B296 — the composition issues above are drawn per issue (each its own
          `role="status"` line) and the door pointed at none of them. It points at the
          list now; when the door is shut for a reason the list does not carry, it says
          that itself rather than leaving the buyer to guess. */}
      {!canAdd(card, comp) && issues.length === 0 ? (
        <p className="t-gate-note" role="note" id="add-gate">
          Choose your tickets above before adding to the cart.
        </p>
      ) : null}
      <Button
        onClick={props.onAdd}
        disabled={!canAdd(card, comp)}
        {...(!canAdd(card, comp) ? { describedBy: issues.length > 0 ? "add-issues" : "add-gate" } : {})}
      >
        {props.addLabel ?? "Add to cart"}
      </Button>
    </div>
  );
}

/** B31h — the persistent cart while browsing; package-owned since B281. */
export { CartBadge } from "@transact/ui";

/**
 * B345 — the departure refusal's one real next step, drawn.
 *
 * The face this replaces satisfied FORMS.md R12 through its PROSE escape
 * (`emptyTeaches: "try another date"`): the harness accepted a taught
 * action in words where no control existed, and the words pointed at a
 * date picker the refusal does not render. So the escape hatch was
 * being held open by the defect itself. A buyer whose chosen ticket has
 * no sellable departure has exactly one move — go back and choose a
 * different ticket — and it is a door, not a sentence.
 */
export function browseAllDoor(onBrowseAll: (() => void) | undefined): ReactNode {
  if (onBrowseAll === undefined) return undefined;
  return (
    <Button variant="neutral" onClick={onBrowseAll}>
      Browse tickets
    </Button>
  );
}

export interface EmptyDepartureFace {
  readonly title: string;
  readonly body: string;
}

/**
 * B345 — the words a departure list says when it has nothing to draw,
 * DERIVED from the condition that produced the refusal.
 *
 * The sentence this replaces was one hedge doing four jobs, and it was
 * wrong three ways at once: it blamed "sold out" (impossible — a
 * sold-out departure is still ON_SALE, so it lands in the list and
 * draws as a disabled tile; this branch is reachable only when nothing
 * is ON_SALE), it narrowed a whole-list emptiness to "this date" (neither
 * picker is given a date), and it told the stranger to "try another date"
 * on the one face where no date control is drawn.
 *
 * Four conditions, four sentences. Nothing here names a cause the branch
 * cannot have, and nothing names a control the face does not draw — an
 * honestly actionless empty state is allowed to be one (DESIGN.md), and
 * the cure for a missing door is never a painted one.
 *
 * Said ONCE and shared, not copied: both pickers read this (B294 rule 2 —
 * two screens showing one sentence is two copies of it). The copy-paste
 * between them is exactly how the old sentence came to be wrong in two
 * places at the same time.
 */
export function emptyDepartureFace(slots: readonly SlotSummary[]): EmptyDepartureFace {
  if (slots.length === 0)
    return {
      title: "No departures scheduled",
      body: "Nothing has been put on the timetable here.",
    };
  const done = (status: string): boolean => status === "CLOSED" || status === "COMPLETED" || status === "CANCELLED";
  if (slots.every((s) => s.status === "DRAFT"))
    return {
      title: "Not on sale",
      body: "These departures are on the timetable but have not been released for sale.",
    };
  if (slots.every((s) => done(s.status)))
    return {
      title: "Sales have closed",
      body: "Every departure on this timetable has closed or already run.",
    };
  return {
    title: "No departures on sale",
    body: "Nothing on this timetable is open for sale.",
  };
}

export function SlotPicker(props: {
  readonly slots: readonly SlotSummary[];
  readonly availability: Readonly<Record<string, readonly PoolAvailability[]>>;
  readonly onPick: (slotId: string) => void;
  readonly formatStart?: (epochMs: number) => string;
  /** B345 — the way back to the catalog, drawn on the refusal. */
  readonly onBrowseAll?: () => void;
}): ReactElement {
  // B142: the default face speaks buyer words — never a machine instant.
  const fmt = props.formatStart ?? ((ms: number) => slotDisplay(ms));
  const onSale = props.slots.filter((s) => s.status === "ON_SALE");
  if (onSale.length === 0) {
    // B345 — the words come from the condition, not from a hedge, and the
    // one real next step is DRAWN rather than named in prose.
    const face = emptyDepartureFace(props.slots);
    return <EmptyState title={face.title} body={face.body} action={browseAllDoor(props.onBrowseAll)} />;
  }
  return (
    <ul className="slot-picker">
      {onSale.map((slot) => {
        // B355 — the derivation moved to browse.ts and is now SHARED with the
        // POS sailing strip. Behavior here is unchanged by construction: this
        // is the same min-across-pools, same honest `undefined` for unread.
        const left = seatsLeft(props.availability[slot.slotId] ?? []);
        const soldOut = left !== undefined && left <= 0;
        return (
          <li key={slot.slotId}>
            <PickerTile
              value={fmt(slot.start)}
              {...(left !== undefined ? { note: soldOut ? "sold out" : `${left} left`, ...(soldOut ? { noteTone: "low" as const } : {}) } : {})}
              disabled={soldOut}
              onPress={() => props.onPick(slot.slotId)}
            />
          </li>
        );
      })}
    </ul>
  );
}

/* CartView and PaymentView RETIRED (B397 leg (3), 73:2): the buyer's cart
   and card fields are ONE merged page now — apps/storefront/src/ui/cartPay.tsx.
   Their walls moved with them (cartPay.test.tsx and friends): B296's named
   pay walls, B312's promo refusal slot, B338's busy doors, B391's line
   steppers (a Pathik ruling the frame does not draw — recorded on the item),
   B31g's at-the-line hints, and the honest authorized face. */

/* ConfirmationView RETIRED here (B397 leg (4), 74:2): the buyer's ticket
   home is its own screen now — apps/storefront/src/ui/confirmationScreen.tsx.
   Its walls moved with it (confirmationScreen.test.tsx): B68(b) tokens
   behind Advanced + QR named for AT, B115 the code leads, B205 the order
   id shows, B306 the package chip, and B115(b) repointed to the printed
   page (the per-row code stamp retired with the card layout, out loud).
   The re-export keeps the app's import surface. */
export { ConfirmationView, type TicketView } from "./confirmationScreen.js";

/**
 * B355 — the frame's footer (storefront-booking-page.png, 75:2): "Secure
 * checkout · Powered by transact". The frame's LEFT side (name · address ·
 * email) states facts no storefront read carries — the channel theme is
 * logo + colors only — so the foot says only what it can back; the read
 * amendment is priced on the board line, not faked here.
 */
/**
 * B397 (75:2) — the footer is space-between: tenant facts left, the
 * platform's line right. No read carries a tenant name, address or email
 * yet (the class-B channel amendment priced on B355's line), so the left
 * slot renders ONLY when a caller has a read-carried sentence to put in
 * it — the honest face of a missing fact is a shorter line, never a
 * placeholder (§10).
 */
export function ShellFoot(props: { readonly facts?: string }): ReactElement {
  return (
    <footer className="shell-foot">
      {props.facts !== undefined ? <p className="shell-foot-facts">{props.facts}</p> : null}
      <p className="shell-foot-line" data-redline="booking-foot-right">Secure checkout · Powered by transact</p>
    </footer>
  );
}

/**
 * B397 (75:2) — the booking page's shell head: channel brand left, the
 * storefront nav and the live cart chip right. The TENANT NAME the frame
 * draws beside the tile is omitted because no read carries one (§10 —
 * same priced amendment as the footer facts); TIMETABLE/HELP exist only
 * if the channel config carries those pages, which today's config cannot
 * state, so the nav is TICKETS alone — never a dead link. `children` is
 * the host app's right-edge extra (the hosted theme switcher).
 */
/**
 * B403 (72:2/73:2) — the flow pages' own head: channel tile + the
 * tenant's name (the mint's businessName — the priced amendment landed
 * with #26) + the page's name. No nav, no cart chip: the sticky bar is
 * the page's one door forward.
 */
export function TicketsPageHead(props: {
  readonly pageName: string;
  readonly logoUrl?: string;
  readonly brandName?: string;
  readonly redline?: string;
  readonly children?: ReactNode;
}): ReactElement {
  return (
    <header className="tickets-page-head" {...(props.redline !== undefined ? { "data-redline": props.redline } : {})}>
      <span className="booking-brand booking-brand--sm">
        {props.logoUrl !== undefined ? (
          <img className="booking-brand-logo booking-brand-logo--sm" src={props.logoUrl} alt="" />
        ) : (
          <span className="booking-brand-tile booking-brand-tile--sm" aria-hidden="true" />
        )}
        {props.brandName !== undefined ? <span className="booking-brand-name booking-brand-name--sm">{props.brandName}</span> : null}
      </span>
      <span className="tickets-page-name">{props.pageName}</span>
      {props.children}
    </header>
  );
}

export function BookingHead(props: {
  readonly logoUrl?: string;
  /** B403 #26 (c) — the tenant's name beside the tile (75:2): the mint's
   * businessName when the tenant stored one; absent stays the tile alone. */
  readonly brandName?: string;
  /**
   * B517 — the cart door exists only where a cart does: an EMBED is one
   * purchase placed at once (Pathik 2026-09-22: "the flow on navy pier does
   * not allow add to cart"), so the embedded head is handed no cart and
   * draws no chip — a door to a cart that can never hold anything lies.
   */
  readonly cartCount?: number;
  readonly onOpenCart?: () => void;
  readonly children?: ReactNode;
}): ReactElement {
  return (
    <GlassHeader redline="booking-head" className="booking-head">
      <span className="booking-brand" data-redline="booking-brand">
        {props.logoUrl !== undefined ? (
          <img className="booking-brand-logo" src={props.logoUrl} alt="" />
        ) : (
          /* the channel's color tile (75:2): the accent IS the channel's, via the theme vars */
          <span className="booking-brand-tile" aria-hidden="true" />
        )}
        {props.brandName !== undefined ? <span className="booking-brand-name">{props.brandName}</span> : null}
      </span>
      <nav className="booking-nav" aria-label="Storefront">
        <span className="booking-nav-item booking-nav-item--active" data-redline="booking-nav-tickets" aria-current="page">
          Tickets
        </span>
      </nav>
      {props.cartCount !== undefined && props.onOpenCart !== undefined ? (
        <CartChip count={props.cartCount} onOpen={props.onOpenCart} redline="booking-cart-chip" />
      ) : null}
      {props.children}
    </GlassHeader>
  );
}

/**
 * B397 (75:2) — the hero: the tenant's catalog summarized as the H1 (a
 * derivation until a channel read carries a headline — the sidecar leaves
 * the wording uncovered on purpose), one teaching sub, and the catalog's
 * own accompaniment rule as the second sentence only when the catalog
 * carries one.
 */
export function BookingHero(props: { readonly title: string; readonly note?: string }): ReactElement {
  return (
    <section className="booking-hero">
      <h1 className="booking-hero-title" data-redline="booking-hero-title">
        {props.title}
      </h1>
      <p className="booking-hero-sub" data-redline="booking-hero-sub">
        Pick what you need — the price you see is the price you pay.
        {props.note !== undefined ? ` ${props.note}` : ""}
      </p>
    </section>
  );
}

export function ExpiredView(props: { readonly onRestart: () => void }): ReactElement {
  /* B238(c) — this face replaces the payment screen UNDER the buyer, on a
     timer they were not watching, and it used to do so as a bare <p>: no
     tone, no live region, nothing an assistive technology would announce.
     B213 rule (i) / B223 / B227's class exactly. The tone is `warn`, not
     `danger`, for B223(b)'s reason — nothing failed and nothing was
     charged, so a danger face would lie about what happened — and the way
     back lives INSIDE the news (B213 rule (ii) / B227), because Start over
     is what the sentence is telling the buyer to do. */
  return (
    <div className="expired">
      <Banner tone="warn">
        Your hold expired and the seats were released. Nothing was charged.
        <Button onClick={props.onRestart}>Start over</Button>
      </Banner>
    </div>
  );
}
